/* =============================================================
   usePartnerById.js — Fetch a single partner university by UUID
   -------------------------------------------------------------
   Used by PartnerProfile and PartnerExchangeHistory to load a
   partner's details from Supabase by their UUID.

   Cache strategy (module-level Map):
     _cache survives React component unmount/remount and token
     refreshes. On every mount the hook returns cached data
     immediately (no spinner), then silently re-fetches to stay
     fresh.

   Usage:
     const { partner, loading, refetch } = usePartnerById(id);

   refetch() invalidates the cache entry and re-fetches — call
   it after updating partner details to reflect changes.

   Linked universities ("Connected" flow):
     If partner_universities.linked_university_id is set, the
     partner's exchange profile fields (useful_links, key_dates,
     contact_emails, exchange_period, campus, business_school,
     language_of_instruction) are overlaid with data from the
     linked university's own profile in the universities table.
     This keeps the profile automatically up to date — the partner
     manages their own data and all connected universities see it.
   ============================================================= */

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const _cache = new Map();

/* ─── mergeLinkedProfile ─────────────────────────────────────── */
// If the partner row has a linked_university_id, fetch that
// university's self-managed profile and overlay the profile fields.
// Returns the merged object (or the original if no link / fetch fails).
async function mergeLinkedProfile(partnerRow) {
  if (!partnerRow?.linked_university_id) return partnerRow;

  try {
    const { data: uni } = await supabase
      .from('universities')
      .select('useful_links, key_dates, contact_emails, exchange_period, campus, business_school, language_of_instruction')
      .eq('id', partnerRow.linked_university_id)
      .maybeSingle();

    if (!uni) return partnerRow;

    // Overlay only non-empty values from the linked university.
    // If the linked university hasn't filled in a field yet, keep
    // whatever the partner manually entered.
    return {
      ...partnerRow,
      useful_links:            (uni.useful_links?.length            ? uni.useful_links            : partnerRow.useful_links)            ?? [],
      key_dates:               (uni.key_dates?.length               ? uni.key_dates               : partnerRow.key_dates)               ?? [],
      contact_emails:          (uni.contact_emails?.length          ? uni.contact_emails          : partnerRow.contact_emails)          ?? [],
      exchange_period:         uni.exchange_period         ?? partnerRow.exchange_period         ?? null,
      campus:                  uni.campus                  ?? partnerRow.campus                  ?? null,
      business_school:         uni.business_school         ?? partnerRow.business_school         ?? null,
      language_of_instruction: uni.language_of_instruction ?? partnerRow.language_of_instruction ?? null,
    };
  } catch (err) {
    console.error('[usePartnerById] mergeLinkedProfile:', err);
    return partnerRow; // fallback to manual data on error
  }
}


export function usePartnerById(id) {
  const _cached = id ? _cache.get(id) : undefined;

  const [partner, setPartner] = useState(_cached ?? null);
  const [loading, setLoading] = useState(_cached === undefined);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    if (!_cache.has(id)) setLoading(true);

    (async () => {
      try {
        const { data, error } = await supabase
          .from('partner_universities')
          .select('*')
          .eq('id', id)
          .maybeSingle();
        if (error) console.error('[usePartnerById] fetch:', error.message);
        if (data) {
          const merged = await mergeLinkedProfile(data);
          setPartner(merged);
          _cache.set(id, merged);
        } else {
          setPartner(null);
        }
      } catch (err) {
        console.error('[usePartnerById] unexpected:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const refetch = async () => {
    if (!id) return;
    try {
      const { data, error } = await supabase
        .from('partner_universities')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) {
        console.error('[usePartnerById] refetch:', error.message);
        return;
      }
      if (data) {
        const merged = await mergeLinkedProfile(data);
        _cache.set(id, merged);
        setPartner(merged);
      }
    } catch (err) {
      console.error('[usePartnerById] refetch unexpected:', err);
    }
  };

  // patchPartner: update cache + state directly with new data,
  // without making an additional Supabase request. Use this after
  // a successful save to reflect changes immediately and safely.
  const patchPartner = (updates) => {
    if (!id) return;
    const current = _cache.get(id) ?? partner ?? {};
    const updated  = { ...current, ...updates };
    _cache.set(id, updated);
    setPartner(updated);
  };

  return { partner, loading, refetch, patchPartner };
}
